"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Clock, Car, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
interface DurationPackage {
  id: string;
  hours: string;
  subtitle: string;
  description: string;
  activities: string[];
}
const durationPackages: DurationPackage[] = [{
  id: "2-hours",
  hours: "2 Stunden",
  subtitle: "Schnelle Besorgungen in der Stadt & Marina",
  description: "Ideal für kurze Fahrten zwischen Hotel, Hurghada Marina, Restaurants und lokalen Einkaufsmöglichkeiten.",
  activities: ["Fahrt zur Hurghada Marina Promenade & zu Restaurants", "Besuch traditioneller Souks im historischen El Dahar", "Kurze Erledigungen & Supermarkt-Einkäufe", "Bequemer Hin- und Rücktransfer zu Ihrem Hotel oder Apartment"]
}, {
  id: "4-hours",
  hours: "4 Stunden",
  subtitle: "Halbtägige Hurghada-Tour",
  description: "Perfekt für entspanntes Sightseeing, Fotostopps an der Küste und Souvenir-Shopping im eigenen Tempo.",
  activities: ["Al-Mina-Moschee & Koptische Kirche St. Schenuda", "Spaziergang an der Marina & Erfrischungspause am Strand", "Lebendige Basare & Gewürzmärkte in El Dahar", "Ihr Fahrer bleibt bei jedem Zwischenstopp auf Abruf bereit"]
}, {
  id: "6-hours",
  hours: "6 Stunden",
  subtitle: "Erweiterter Küstenausflug",
  description: "Erweiterte Küstenroute: Route, Stopps, Dauer und Festpreis werden vor der Buchung transparent mit Ihnen abgestimmt und bestätigt.",
  activities: ["Ausflug zu den Lagunen von El Gouna oder zur Seebrücke von Sahl Hasheesh", "Gemütliches Mittagessen am Meer & entspannte Badezeit", "Mehrere malerische Aussichtspunkte entlang des Roten Meeres", "Klimatisierte Fahrt mit vorab abgestimmten Haltezeiten und Stopps"]
}, {
  id: "8-hours",
  hours: "8 Stunden",
  subtitle: "Ganztägige individuelle Route",
  description: "Ganztägiger Chauffeur-Service: Route, gewünschte Stopps, Dauer und Festpreis werden vor der Buchung verbindlich mit Ihnen bestätigt.",
  activities: ["Vollständig maßgeschneiderte Reiseroute für Ihre Gruppe", "Kombination aus El Gouna, Hurghada Marina & Makadi Bay", "Aussichtspunkte zum Sonnenuntergang, Abendessen-Transfers & Spaziergänge", "Vorab bestätigte Zwischenstopps und kalkulierter Ablauf nach Absprache"]
}];
export default function DurationSelect() {
  const handleSelectDuration = (hoursValue: string) => {
    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem("phd_preselected_duration", hoursValue);
        window.dispatchEvent(new CustomEvent("phd-preselect-duration", {
          detail: hoursValue
        }));
      } catch (err) {
        console.error("Failed to store selected duration", err);
      }
      const targetEl = document.getElementById("privatecardriverhero");
      if (targetEl) {
        targetEl.scrollIntoView({
          behavior: "smooth"
        });
      }
    }
  };
  return <section id="chooseyourduration" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-14 lg:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-semibold mb-4 tracking-wide uppercase">
            <Clock className="w-3.5 h-3.5" />
            <span>Stundenweise Privatmiete</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-accent-foreground font-default">
            Wählen Sie Ihre Dauer
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Buchen Sie ein eigenes Fahrzeug mit lokalem Fahrer stundenweise. Reisen Sie mit voller Flexibilität wohin Sie möchten in Hurghada.
          </p>
        </div>

        {/* 4-Column Duration Grid */}
        <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-4 items-stretch">
          {durationPackages.map((pkg, index) => <motion.div key={pkg.id} data-index={index} initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: index * 0.06,
          ease: [0.16, 1, 0.3, 1]
        }} className="group relative flex flex-col justify-between bg-card text-card-foreground border border-border rounded-3xl p-6 sm:p-7 shadow-sm transition-colors duration-200 hover:border-primary">
              <div>
                {/* Header with Icons */}
                <div className="flex items-center justify-between gap-3 mb-5">
                  <div className="inline-flex items-center justify-center size-12 rounded-2xl bg-accent text-accent-foreground">
                    <Clock className="size-6 text-accent-foreground" />
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background border border-border text-xs font-medium text-muted-foreground">
                    <Car className="size-3.5 text-primary" />
                    <span>Privatwagen</span>
                  </div>
                </div>

                {/* Duration Title & Subtitle */}
                <div className="space-y-1.5 mb-4">
                  <h3 className="text-2xl sm:text-3xl font-bold text-accent-foreground tracking-tight">
                    {pkg.hours}
                  </h3>
                  <p className="text-sm font-semibold text-foreground/90">
                    {pkg.subtitle}
                  </p>
                </div>

                {/* Body Text */}
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed mb-6">
                  {pkg.description}
                </p>

                {/* Bullet Points */}
                <div className="border-t border-border/80 pt-5 mb-8">
                  <span className="block text-xs font-semibold text-accent-foreground uppercase tracking-wider mb-3">
                    Typischer Ablauf
                  </span>
                  <ul className="space-y-2.5 text-xs sm:text-sm text-muted-foreground">
                    {pkg.activities.map((activity, actIndex) => <li key={actIndex} className="flex items-start gap-2.5">
                        <Check className="size-4 shrink-0 text-primary mt-0.5" />
                        <span className="leading-snug">{activity}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              {/* Action Button */}
              <div className="pt-2">
                <Button type="button" variant="default" onClick={() => handleSelectDuration(pkg.hours)} className="w-full h-11 rounded-xl font-medium justify-center gap-2 group-hover:bg-primary/95 transition-all shadow-xs cursor-pointer">
                  <span>{pkg.hours} auswählen</span>
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Button>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}